import React from 'react';
import { get } from 'lodash';
import { styled, Icon, Flex, Modal } from '@goodrx/matisse';
import { useToggledState } from 'app/hooks';
import { getCouponTerm } from 'app/lib/coupon';
import { Coupon as CouponType } from 'app/types/Coupon';

const VerticalColumn = styled(Flex)`
  flex-direction: column;
  align-items: center;
`;

const EstimateDisclaimer = styled(VerticalColumn)`
  margin-top: 16px;
  text-align: center;
  align-items: center;
  flex-direction: row;
  cursor: pointer;
  color: ${(p) => p.theme.colors.greyDarkest};
`;

const InsuranceDisclaimer = styled(VerticalColumn)`
  margin-top: 16px;
  text-align: normal;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  cursor: pointer;
`;

const InsuranceDisclaimerText = styled.span`
  color: ${(p) => p.theme.colors.greyDarkest};
`;

const InformationIcon = styled(Icon)`
  margin-left: 8px;
`;

const Text = styled.span`
  color: ${(p) => p.theme.colors.greyDark};
  margin-bottom: 8px;
  display: block;
`;

interface InsuranceDisclaimerModalProps {
  open: any;
  pharmacyName: any;
  couponTerm: any;
}

const InsuranceDisclaimerModal: React.FC<InsuranceDisclaimerModalProps> = ({ open, pharmacyName, couponTerm }) => {
  const titleTerm = couponTerm === 'coupon' ? 'Discount Coupon' : 'Discount Card';
  const textTerm = couponTerm === 'coupon' ? 'coupon card' : 'discount card';

  return (
    <Modal open={open} maxWidth={350} title={`How to use the ${titleTerm}`}>
      <Modal.Body>
        <Text>
          Present this {textTerm} to the pharmacist when filling or refilling your prescription. Once you use a{' '}
          {textTerm}, it will remain on file at your pharmacy and can be used for any medication you fill or refill
          there.
        </Text>

        <Text>
          Some pharmacies may not be aware that they are contractually obligated to honor GoodRx {textTerm}s. Start by
          calling the phone number on the {textTerm}, and if that doesn&#39;t resolve the problem, contact us at
          <a href="mailto:info@goodrx.com"> info@goodrx.com</a>.
        </Text>

        <Text>
          This {textTerm} at {pharmacyName} can be used by any member of your family and will work at other pharmacies,
          though your price may vary. This is not insurance. Cannot be used in conjunction with Medicare or Medicaid.
        </Text>
      </Modal.Body>
    </Modal>
  );
};

interface CouponDisclaimerProps {
  coupon?: CouponType;
}

export const CouponDisclaimer: React.FC<CouponDisclaimerProps> = ({ coupon }) => {
  const [isDisclaimerModalOpen, toggleInsuranceDisclaimerModal] = useToggledState(false);

  if (!coupon) return null;

  const couponTerm = getCouponTerm(coupon);
  const pharmacy = get(coupon, 'pharmacy.name');

  return (
    <>
      <EstimateDisclaimer mt={0}>
        This is your price estimate at {pharmacy}. Your pharmacy will provide the exact pricing.
      </EstimateDisclaimer>

      <InsuranceDisclaimer mt={0} onClick={toggleInsuranceDisclaimerModal} role="button">
        <InsuranceDisclaimerText>This is not insurance</InsuranceDisclaimerText>
        <InformationIcon name="information-outline" color="greyDarkest" />
        <InsuranceDisclaimerModal open={isDisclaimerModalOpen} pharmacyName={pharmacy} couponTerm={couponTerm} />
      </InsuranceDisclaimer>
    </>
  );
};
